/*
 * Copyright (c) 2021.
 * 2iPartners SàRL and/or its affiliates.
 * All rights reserved.
 *
 * 2iPartners SàRL
 * Route de Compois 24,
 * 1252 Meinier
 * Suisse
 *
 * tel : +41 22 735 97 07
 */

import {Component, Inject, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ExternalImportRemovalService} from '../../../_services/external-import-removal.service';
import {ToastrService} from 'ngx-toastr';
import {ExternalImportAnimal} from '../../../_models/external-import-animal';
import {ExternalImportRemoval} from '../../../_models/external-import-removal';
import {AnimalImportType} from '../../../_forms/animal-import-type';
import {RemovalImportType} from '../../../_forms/removal-import-type';
import {Helpers} from '../../../helpers';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {Removal} from '../../../_models/removal';
import {I18nService} from '../../../_services/i18n.service';
import {OK, PARTIAL_CONTENT} from '../../../_enums/requests';

@Component({
  selector: 'app-dialog-removal-validate',
  templateUrl: './dialog-removal-validate.component.html',
  styleUrls: ['./dialog-removal-validate.component.scss']
})
export class DialogRemovalValidateComponent implements OnInit {

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PROPERTIES                                                                                            ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public showDocsToGive = false;
  public showChoicePayment = false;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORMS GROUPS                                                                                          ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public removalFG: FormGroup;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              MAGICS METHODS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  constructor(
    public dialogRef: MatDialogRef<DialogRemovalValidateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogValidateRemovalData,
    private formBuilder: FormBuilder,
    private i18n: I18nService,
    private eIRemovalService: ExternalImportRemovalService,
    private toastr: ToastrService
  ) {
  }

  ngOnInit() {
    this.initRemovalForm();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              REMOVAL FORM CONTROLS                                                                                 ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get paymentType(): AbstractControl {
    return this.removalFG.get('paymentType');
  }

  get generalConditions(): AbstractControl {
    return this.removalFG.get('generalConditions');
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              VALUES GETTERS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get externalAnimals(): ExternalImportAnimal[] {
    return (this.data && this.data.externalRemoval) ? this.data.externalRemoval.animals : [];
  }

  get externalRemoval(): ExternalImportRemoval | null {
    return this.data ? this.data.externalRemoval : null;
  }

  get submittedAnimals(): AnimalImportType[] {
    return (this.data && this.data.submittedRemoval) ? this.data.submittedRemoval.animals : [];
  }

  get submittedRemoval(): RemovalImportType | null {
    return this.data ? this.data.submittedRemoval : null;
  }


  get hasAnimalNotAdded(): boolean {
    return this.externalAnimals.length !== this.submittedAnimals.length;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORMS INITIALISATION                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private initRemovalForm() {
    this.removalFG = this.formBuilder.group({
      paymentType: [null, Validators.required],
      generalConditions: [null, Validators.requiredTrue]
    });
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORMS SUBMITS                                                                                         ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public cancel(): void {
    if (this.externalRemoval) {
      Helpers.setLoading(true);

      this.eIRemovalService.deleteById(this.externalRemoval.id, {version: 0.3}).subscribe(() => {
        Helpers.setLoading(false);
        this.close(null);
      }, (err: HttpErrorResponse) => this.handleError(err));
    } else {
      this.close(null);
    }
  }

  public close(res: Removal | null) {
    this.dialogRef.close(res);
  }

  public removalFormSubmit() {
    if (this.removalFG.valid) {
      Helpers.setLoading(true);

      this.eIRemovalService.confirm(this.externalRemoval.id, {
          generalConditions: this.generalConditions.value,
          paymentType: this.paymentType.value
        }, {
          version: 0.3
        }
      ).subscribe((res: HttpResponse<Removal | string>) => {
        Helpers.setLoading(false);

        if (res.status === OK) {
          this.close(res.body as Removal);
        } else if (res.status === PARTIAL_CONTENT) {
          window.location.href = res.body as string;
        }
      }, (err: HttpErrorResponse) => this.handleError(err));
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              LISTENERS                                                                                             ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public hasDocsToGive(e: boolean) {
    this.showDocsToGive = e;
  }

  public needPayment(e: boolean) {
    this.showChoicePayment = e;

    if (this.showChoicePayment) {
      this.paymentType.setValidators([Validators.required]);
    } else {
      this.paymentType.clearValidators();
    }

    this.paymentType.updateValueAndValidity();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PRIVATE METHODS                                                                                       ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private handleError(err: HttpErrorResponse): void {
    Helpers.setLoading(false);

    this.toastr.error(`${err.status} - ${this.i18n.get(err.status)}`);
  }

}

export interface DialogValidateRemovalData {
  externalRemoval: ExternalImportRemoval;
  submittedRemoval: RemovalImportType;
}
